import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { NoiseOverlay } from "@/components/NoiseOverlay";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import ThemeSelect from "@/pages/ThemeSelect";
import Onboarding from "@/pages/Onboarding";
import Choose from "@/pages/Choose";
import Chat from "@/pages/Chat";
import Journey from "@/pages/Journey";
import History from "@/pages/History";
import Reviews from "@/pages/Reviews";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <span className="thinking-dot text-[var(--mirror-text-3)] text-sm tracking-[0.3em] uppercase">
      mirror
    </span>
  </div>
);

const Protected = ({ children, requireOnboarded = true }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/auth" replace />;
  if (requireOnboarded && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <NoiseOverlay />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#22223B",
            border: "1px solid rgba(242,236,228,0.1)",
            color: "#F2ECE4",
          },
        }}
      />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Process an OAuth return (session_id in the URL fragment) BEFORE any route /
  // protected-route logic runs, to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route
        path="/theme"
        element={
          <Protected requireOnboarded={false}>
            <ThemeSelect />
          </Protected>
        }
      />
      <Route
        path="/onboarding"
        element={
          <Protected requireOnboarded={false}>
            <Onboarding />
          </Protected>
        }
      />
      <Route
        path="/settings"
        element={
          <Protected>
            <Settings />
          </Protected>
        }
      />
      <Route
        path="/begin"
        element={
          <Protected>
            <Choose />
          </Protected>
        }
      />
      <Route
        path="/chat/:id"
        element={
          <Protected>
            <Chat />
          </Protected>
        }
      />
      <Route
        path="/journey"
        element={
          <Protected>
            <Journey />
          </Protected>
        }
      />
      <Route
        path="/history"
        element={
          <Protected>
            <History />
          </Protected>
        }
      />
      <Route
        path="/reviews"
        element={
          <Protected>
            <Reviews />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function Root() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ThemeProvider>
  );
}
